import React, { useEffect, useState } from "react";
import { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ChangeNetworkDrawer from "../../Modules/ChangeNetworkDrawer";
import NetworkUnsupportedModal from "../../Modules/NetworkUnsupportedModal";
import PreflightModalTransfer from "../../Modules/PreflightModalTransfer";
import AddressInput from "../Custom/AddressInput";
import TransferActiveModal from "../../Modules/TransferActiveModal";
import { useWeb3 } from "@chainsafe/web3-context";
import {
  NFTTokenDeclaration,
  useChainbridge,
} from "../../Contexts/ChainbridgeContext";
import NFTTokenSelectInput from "../Custom/NFTTokenSelectInput";
import NFTTokenIdSelectInput from "../Custom/NFTTokenIdSelectInput";

import { BigNumber, ethers, utils } from "ethers";
import { chainbridgeConfig } from "../../chainbridgeConfig";
import Fees from "../Custom/Fees";
import PageHeading from "../UI/PageHeading";
import {
  Heading,
  Text,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Box,
  Stack,
  Flex,
  Button,
  Alert,
  AlertIcon,
  Image,
  useToast,
  Divider,
  VStack,
  Link,
} from "@chakra-ui/react";
import { ExternalLinkIcon, TimeIcon } from "@chakra-ui/icons";
import NFTDisplay from "../Custom/NFTDisplay";
import CurrentGasFees from "../Custom/CurrentGasFees";
import { toastSuccess, toastError } from "../../config/toast";
import walletService from "../../Services/WalletService";
import PATCH from "../../media/logos/patch.png";
import CHAINSAFE from "../../media/logos/chainsafe.png";
import CONSENSYS from "../../media/logos/consensys.svg";
import { getFeatureFlagStatus } from "../../featureFlags";

const shadowForkMessagesEnabled = getFeatureFlagStatus(
  "shadowForkMessagesEnabled"
);

type PreflightDetails = {
  tokenId?: string;
  token?: NFTTokenDeclaration;
  receiver?: string;
};

const NFTTransferPage = () => {
  const { isReady, checkIsReady, wallet, onboard, tokens, address, network } =
    useWeb3();
  const {
    homeChain,
    destinationChains,
    destinationChain,
    deposit,
    setDestinationChain,
    transactionStatus,
    resetDeposit,
    bridgeFee,
    homeChainIsPalm,
    palmRelayFee,
    palmGreenFee,
    tollTokenAddress,
    tollTokenSymbol,
    tollTokenDecimals,
    nftTokens,
    updateTokenIdsOwnedForNFT,
    ownedTokenIdsByTokenAddress,
    updateMetadataForTokenId,
    tokenMetadataByTokenAddressAndId,
  } = useChainbridge();

  const toast = useToast();

  const [walletConnecting, setWalletConnecting] = useState(false);
  const [preflightModalOpen, setPreflightModalOpen] = useState<boolean>(false);
  const [switchingNetworks, setSwitchingNetworks] = useState<boolean>(false);

  const [preflightDetails, setPreflightDetails] = useState<PreflightDetails>({
    receiver: "",
    token: undefined,
    tokenId: "",
  });

  useEffect(() => {
    if (!preflightDetails.token) {
      return;
    }

    updateTokenIdsOwnedForNFT(preflightDetails.token.contract);
  }, [preflightDetails.token]);

  useEffect(() => {
    if (!preflightDetails.token || !preflightDetails.tokenId) {
      return;
    }

    updateMetadataForTokenId(
      preflightDetails.token.contract,
      preflightDetails.tokenId
    );
  }, [preflightDetails.token, preflightDetails.tokenId]);

  const handleConnect = async () => {
    setWalletConnecting(true);
    !wallet && (await onboard?.walletSelect());
    wallet && (await checkIsReady());
    setWalletConnecting(false);
  };

  const totalFees = () => {
    const relayFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals))
      : bridgeFee;
    const greenFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals))
      : 0;
    return relayFee ? relayFee + greenFee : greenFee;
  };

  const transferSchema = object().shape({
    tokenId: string()
      .test("Token selected", "Please select an NFT", (value) => {
        if (
          !!value &&
          preflightDetails.token &&
          ownedTokenIdsByTokenAddress[preflightDetails.token.address]
            .map((bigNumber) => bigNumber.toString())
            .includes(value)
        ) {
          return true;
        } else {
          return false;
        }
      })
      .required("Please set a value"),
    token: string().required("Please select a token"),
    receiver: string()
      .test("Valid address", "Please add a valid address", (value) => {
        return utils.isAddress(value as string);
      })
      .test("Fees", "Insufficient funds to pay bridge fees", (value) => {
        const bridgeFees = totalFees();
        if (bridgeFees == 0 || !homeChainIsPalm) {
          return true;
        }
        if (
          tollTokenAddress &&
          tokens[tollTokenAddress] &&
          tokens[tollTokenAddress].balance
        ) {
          return bridgeFees <= tokens[tollTokenAddress].balance;
        }
        return false;
      })
      .required("Please add a receiving address"),
  });

  // TODO: line 467: How to pull correct HomeChain Symbol

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<{
    token: string;
    tokenId: string;
    receiver: string;
  }>({
    mode: "onBlur",
    resolver: yupResolver(transferSchema),
  });

  const resetForm = async () => {
    setPreflightDetails({
      receiver: "",
      token: undefined,
      tokenId: "",
    });
    reset({ token: "" });
  };

  const handleSwitchNetwork = async (chainName?: string, chainId?: number) => {
    const id = chainId || destinationChain?.networkId;
    const name = (chainId && chainName) || destinationChain?.name;

    const showToast = (success: boolean) => {
      const title = success
        ? `Chain changed to ${name}`
        : `Sorry, there was an error changing chains`;

      return toast({
        title,
        ...(success ? toastSuccess : toastError),
      });
    };

    if (id) {
      setSwitchingNetworks(true);
      walletService
        .switchNetwork(id)
        .then((result: any) => {
          if (result) {
            return showToast(true);
          } else {
            return showToast(false);
          }
        })
        .catch((error) => {
          return showToast(false);
        })
        .finally(() => setSwitchingNetworks(false));
    }
  };

  return (
    <>
      <PageHeading title="NFT Bridge" />
      {shadowForkMessagesEnabled && (
        <Box mb="16px" pl="8px" color="palmNetwork.100" bg="palmNetwork.800">
          <Box pb="32px" pt="48px" maxW="728px" pl="16px">
            <Heading fontSize="24px" mb="16px">
              Shadow Fork Testnet testing
            </Heading>
            <Text mb="16px">
              This application has been updated so that the Palm-Goerli testnet
              bridge now interacts with the temporary &apos;shadow fork&apos;
              Palm Testnet. Bridging between Goerli and the official Palm
              Testnet is currently suspended whilst this testing is carried out.
            </Text>
            <Text mb="16px">
              To use this bridge when sending tokens from the shadow fork Palm
              Testnet to Goerli, you will need to update your wallet to use the
              shadow fork Palm Testnet RPC - this can be added from the
              Configure page.
            </Text>
            <Text mb="16px">
              Please refer to the Palm network documentation for details at{" "}
              <Link>https://docs.palm.io/network-update</Link>
            </Text>
          </Box>
        </Box>
      )}

      <Box
        width="540px"
        bg="white"
        border="1px"
        borderColor="brand.300"
        p="32px"
        ml="18px"
        mt="8px"
      >
        {!isReady ? (
          <Button
            // fullsize
            onClick={() => {
              handleConnect();
            }}
          >
            {!address ? "Connect Wallet" : "Check Wallet"}
          </Button>
        ) : walletConnecting ? (
          <p>
            This app requires access to your wallet, <br />
            please login and authorize access to continue.
          </p>
        ) : (
          <>
            <NetworkDisplay location="Origin Network" name={homeChain?.name} />
            <Button
              width="0px"
              mt="8px"
              mb="10px"
              onClick={() => handleSwitchNetwork()}
              disabled={switchingNetworks}
            >
              ↑↓
            </Button>
            <NetworkDisplay
              location="Destination network"
              name={destinationChain?.name}
            />
          </>
        )}
        <form onSubmit={handleSubmit((data) => setPreflightModalOpen(true))}>
          <Box as={Stack} spacing="50px" direction={"column"} mt="34px">
            <Box as={Stack} spacing="24px" direction={"row"}>
              <FormControl
                isInvalid={!!errors?.token?.message}
                errortext={errors?.token?.message}
                isRequired
              >
                <FormLabel>Token</FormLabel>
                <NFTTokenSelectInput
                  nftTokens={nftTokens}
                  name="token"
                  isDisabled={!destinationChain}
                  sync={(selectedToken: NFTTokenDeclaration) => {
                    setPreflightDetails({
                      ...preflightDetails,
                      token: selectedToken,
                    });
                  }}
                  register={register}
                  setValue={setValue}
                  value={getValues("token")}
                  options={
                    nftTokens.map((declaration, index) => ({
                      value: declaration.address,
                      label: (
                        <div>
                          <span>{nftTokens[index].name}</span>
                        </div>
                      ),
                    })) || []
                  }
                />
                <FormErrorMessage>{errors?.token?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors?.tokenId?.message}
                errortext={errors?.tokenId?.message}
                isRequired
              >
                <FormLabel>NFT ID</FormLabel>
                <NFTTokenIdSelectInput
                  selectedTokenIds={
                    ownedTokenIdsByTokenAddress[
                      preflightDetails.token?.address || ""
                    ] || []
                  }
                  selectedTokenId={preflightDetails.tokenId}
                  name="tokenId"
                  value={getValues("tokenId")}
                  isDisabled={!destinationChain}
                  sync={(tokenIdAsString) => {
                    setPreflightDetails({
                      ...preflightDetails,
                      tokenId: tokenIdAsString,
                    });
                  }}
                  register={register}
                  setValue={setValue}
                />
                <FormErrorMessage>{errors?.tokenId?.message}</FormErrorMessage>
              </FormControl>
            </Box>
            <NFTDisplay
              metadata={
                tokenMetadataByTokenAddressAndId[
                  preflightDetails.token?.address || ""
                ]
                  ? tokenMetadataByTokenAddressAndId[
                      preflightDetails.token?.address || ""
                    ][preflightDetails.tokenId || ""]
                  : undefined
              }
            />

            <FormControl
              isInvalid={!!errors?.receiver?.message}
              errortext={errors?.receiver?.message}
              isRequired
            >
              <FormLabel>Destination address</FormLabel>
              <AddressInput
                name="receiver"
                isDisabled={!destinationChain}
                placeholder="Please enter the receiving address"
                senderAddress={`${address}`}
                value={preflightDetails.receiver || ""}
                onChange={(value: string) => {
                  setPreflightDetails({
                    ...preflightDetails,
                    receiver: value,
                  });
                }}
                register={register}
                setValue={setValue}
              />
              <FormErrorMessage>{errors?.receiver?.message}</FormErrorMessage>
            </FormControl>
          </Box>
          {homeChainIsPalm ? (
            <Alert status="info" mt={4} mb={2}>
              <TimeIcon mr={2} />
              Minimum transfer time: 1 hour (256 blocks)
            </Alert>
          ) : null}
          <Box>
            <CurrentGasFees />
          </Box>
          <Fees
            relayFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals)
                  )
                : bridgeFee
            }
            relayFeeSymbol={
              homeChainIsPalm ? tollTokenSymbol : homeChain?.nativeTokenSymbol
            }
            showGreenFee={homeChainIsPalm}
            greenFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals)
                  )
                : undefined
            }
            greenFeeSymbol={homeChainIsPalm ? tollTokenSymbol : undefined}
            showTransferAmount={false}
          />
          <Flex justifyContent="center">
            <Button
              type="submit"
              isDisabled={
                !!errors.receiver || !!errors.token || !!errors.tokenId
              }
            >
              Start transfer
            </Button>
          </Flex>
        </form>
        <NetworkUnsupportedModal
          open={!homeChain && isReady}
          network={network}
          supportedNetworks={chainbridgeConfig.chains.map((bc) => bc.networkId)}
        />
        <PreflightModalTransfer
          open={preflightModalOpen}
          close={() => setPreflightModalOpen(false)}
          receiver={preflightDetails?.receiver || ""}
          sender={address || ""}
          start={() => {
            setPreflightModalOpen(false);
            preflightDetails.token &&
              preflightDetails.receiver &&
              deposit({
                tokenAddress: preflightDetails.token.address,
                tokenId: preflightDetails.tokenId,
                recipient: preflightDetails.receiver,
              });
          }}
          sourceNetwork={homeChain?.name || ""}
          targetNetwork={destinationChain?.name || ""}
          tokenSymbol={preflightDetails.token?.symbol || ""}
          tokenId={preflightDetails?.tokenId || ""}
        />
        <TransferActiveModal
          open={!!transactionStatus}
          close={() => {
            resetDeposit();
            resetForm();
          }}
        />
        <Divider color="palmNetwork.100" pt="24px" />
        <Box
          as={Stack}
          spacing="22px"
          direction={"column"}
          align="left"
          width="540px"
          mt="36px"
        >
          <Box as={Stack} spacing="8px" direction={"row"} align="center">
            <Text fontSize="xs">Bridge operator: </Text>
            <Image src={CONSENSYS} height="32px" alt="Consensys" />
            <Text fontSize="xs">Carbon offsets: </Text>
            <Image src={PATCH} width="64px" alt="Patch" />
          </Box>
          <Box as={VStack} spacing="1px" direction={"row"} align="left">
            <Text fontSize="xs">
              <Link
                fontSize="xs"
                href={"https://consensys.net/privacy-policy/"}
                isExternal
              >
                Privacy Policy &nbsp;
                <ExternalLinkIcon />
              </Link>{" "}
              |{" "}
              <Link
                fontSize="xs"
                href={"https://consensys.net/terms-of-use/"}
                isExternal
              >
                Terms of Use &nbsp;
                <ExternalLinkIcon />
              </Link>
            </Text>
          </Box>
        </Box>
      </Box>
    </>
  );
};
export default NFTTransferPage;

interface INetworkDisplay {
  location: string;
  name: any;
}

const NetworkDisplay: React.FC<INetworkDisplay> = ({
  location,
  name,
}: INetworkDisplay) => {
  return (
    <>
      <Text fontSize="12px" mb="4px">
        {location}
      </Text>
      <Heading fontSize="20px">{name}</Heading>
    </>
  );
};

interface ICTASection {
  title?: any;
  desc?: any;
  children: ReactNode | ReactNode[];
}

const CTASection: React.FC<ICTASection> = ({
  title,
  desc,
  children,
}: ICTASection) => {
  return (
    <Box pb="48px" pt="48px" maxW="728px" pl="16px">
      <Heading fontSize="24px" mb="16px">
        {title}
      </Heading>
      <Text mb="32px">{desc}</Text>
      <Box display="flex" justifyContent="flex-start">
        {children}
      </Box>
    </Box>
  );
};
